import { ReplaySubject, Subject } from 'rxjs';

/**
 * Bridge between the fetch epic and the render plugin.
 * The epic calls `.next(resource)` once per successful community site map fetch.
 * The plugin subscribes in `start()` and unsubscribes in `stop()`.
 *
 * ReplaySubject(1) is used so that if the epic resolves before the plugin's
 * start() subscribes (a likely race: the epic fires on mapLibraryFetch while
 * start() runs on mapLibraryLoaded), the last value is still delivered.
 * @type {ReplaySubject<SiteMapResource>}
 */
const siteMapResource$ = new ReplaySubject(1);

/**
 * Tracking bridge: plugins/tracking.plugin.js detects map zoom/pan/map-type
 * interactions and calls `.next()` here with the raw payload — this feature has no analytics
 * knowledge of its own (no segmentTrack/googleAnalytics4Event/getAnalyticsTrackingProperties
 * imports anywhere in it). Whichever page embeds this feature subscribes to this — imported via
 * this feature's index.js, not this file directly — and turns the payload into an actual
 * Segment/GA4 dispatch using the build*Event functions in services/segmentEventBuilder.js and
 * services/googleAnalytics4EventBuilder.js, both of which accept exactly this payload shape.
 *
 * Plain Subject, not ReplaySubject — a tracking consumer that subscribes late has nothing
 * useful to catch up on for a one-off interaction.
 * @type {Subject<{ interactionType: string, inputMethod: string, mapType?: string }>}
 */
const mapInteractionOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the listing panel/drawer is updated to a new view — either by an
 * active lot-status chip change (a pill click, `source: 'lot_status_type'`) or by selecting a lot
 * on the map (`source: 'lot_selection'`). `selectedChip`/`previousChip` are only meaningful for
 * the pill path and left null for the lot path. See mapInteractionOccurred$ above for the general
 * pattern this follows.
 * @type {Subject<{ selectedChip: string|null, previousChip: string|null, resultsCount: number,
 *   cardCount: number, source: 'lot_status_type'|'lot_selection', isMobile: boolean }>}
 */
const panelUpdatedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the mobile lot-status pill bar is scrolled. No payload.
 * See mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<void>}
 */
const panelScrollOccurred$ = new Subject();

/**
 * Tracking bridge: emits on a mobile drawer interaction — either a vertical drag or the
 * collapsed→open transition (opening is a tap, not a drag, but is reported as the same
 * interaction). Mobile-only (the caller gates on isMobile); no payload. See mapInteractionOccurred$
 * above for the general pattern this follows.
 * @type {Subject<void>}
 */
const drawerDraggedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a lot is selected on the map.
 * See mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<{ lot: object, planId: number|null, specId: number|null, plansCount: number, isMobile: boolean }>}
 */
const lotSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the listing panel's card grid (Floor Plans, Quick Move-Ins, Model,
 * Coming Soon, or Sold) is scrolled horizontally. Fires on both desktop and mobile — unlike
 * panelScrollOccurred$ (the lot-status pill bar), which is mobile-only. See mapInteractionOccurred$
 * above for the general pattern this follows.
 * @type {Subject<{ isMobile: boolean }>}
 */
const cardsScrolledOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the Available chip's selected tab changes — either a manual click
 * (handlers/tabInteractionHandlers.js) or any other programmatic change to selectedTab (e.g. an
 * automatic fallback if one is added). This Subject observes Redux state via store$, not a specific
 * action's dispatch site, so it fires for any cause that changes the tab. Never fires for the initial
 * default tab on mount, nor when tabs are torn down entirely (switching to a non-Available chip sets
 * selectedTab to null — see redux/dataSlice.js#chipSelected). See mapInteractionOccurred$ above for
 * the general pattern this follows.
 * @type {Subject<{ selectedTab: string, previousTab: string, resultsCount: number, isMobile: boolean }>}
 */
const tabSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the "Get Notified When Available" button (Coming Soon panel) is
 * clicked. No payload. See mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<void>}
 */
const notifyWhenAvailableClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the "Similar homes available" accordion (under a Sold Home Card)
 * is expanded or collapsed. Desktop only — the caller (plugins/tracking.plugin.js) gates on
 * isMobile before calling .next(), since the toggle markup exists on both breakpoints
 * (components/similarHomesSection.dom.js) but only desktop is tracked per the source spec.
 * `expanded` reflects the state AFTER the toggle. See mapInteractionOccurred$ above for the
 * general pattern this follows.
 * @type {Subject<{ expanded: boolean, lotId: number, similarHomesCount: number }>}
 */
const similarHomesToggledOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a similar-home card (under a Sold Home Card's "Similar homes
 * available" list) is clicked. Fires on both desktop and mobile. `planId`/`specId` are mutually
 * exclusive — a candidate is either a floor plan or a housing unit (see
 * components/similarHomesSection.dom.js#renderSimilarHomeCard), never both. `position` is the
 * card's 1-based index within the rendered similar-homes list. See mapInteractionOccurred$ above
 * for the general pattern this follows.
 * @type {Subject<{ lotId: number, planId: number|null, specId: number|null, position: number }>}
 */
const similarHomeCardClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the mobile-only Sold list "See All" button
 * (components/seeAllButton.dom.js, [data-sold-see-all-button]) is clicked. No payload. See
 * mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<void>}
 */
const soldSeeAllClickedOccurred$ = new Subject();

export default siteMapResource$;
export {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
};